import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ListX } from "lucide-react";
import { Toaster, toast } from "../ui/sonner";
import { getToastPosition, subscribeToastPosition } from "../../lib/toastPrefs";

/**
 * Único Toaster do sistema + botão flutuante "limpar todos".
 *
 * O botão é posicionado a partir do retângulo do aviso da frente (fixed no
 * body). Antes ele era injetado ao lado do "x" do aviso — e como a maioria
 * dos avisos NÃO tem botão de fechar (só os persistentes, `toast_sec = 0`),
 * o "limpar tudo" simplesmente não aparecia quando as notificações
 * acumulavam.
 */
export default function GlobalToaster() {
  const [position, setPosition] = useState(getToastPosition());
  const [target, setTarget] = useState(null); // { rect, count }
  // Drawer/Dialog aberto? Os avisos ficam acima de tudo (z-index máximo) para
  // continuarem clicáveis, e por isso cobriam o "x" do drawer — com aviso
  // persistente (novo serviço) o serviço ficava impossível de fechar.
  const [modalAberto, setModalAberto] = useState(false);

  useEffect(() => subscribeToastPosition(setPosition), []);

  // Esc dispensa todos os avisos abertos.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") toast.dismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // O Sonner não expõe a pilha: acompanhamos pelo DOM.
  useEffect(() => {
    const tick = () => {
      // O Radix zera os cliques do body enquanto um modal está aberto — é o
      // sinal mais confiável de "tem drawer/dialog na frente".
      setModalAberto(
        (prev) => {
          const aberto = getComputedStyle(document.body).pointerEvents === "none";
          return prev === aberto ? prev : aberto;
        },
      );
      const els = Array.from(document.querySelectorAll("[data-sonner-toast]")).filter(
        (el) => el.getAttribute("data-removed") !== "true",
      );
      if (els.length < 2) {
        setTarget((t) => (t ? null : t));
        return;
      }
      const front = els.find((el) => el.getAttribute("data-front") === "true") || els[0];
      const r = front.getBoundingClientRect();
      if (!r.width || !r.height) {
        setTarget((t) => (t ? null : t));
        return;
      }
      const next = {
        top: Math.round(r.top - 10),
        left: Math.round(r.right - 12),
        count: els.length,
      };
      setTarget((prev) =>
        prev && prev.top === next.top && prev.left === next.left && prev.count === next.count
          ? prev
          : next,
      );
    };
    const iv = setInterval(tick, 200);
    return () => clearInterval(iv);
  }, []);

  // Com drawer aberto, desce/afasta os avisos do cabeçalho: o "x" do drawer
  // mora no topo e os avisos passam por cima dele.
  const desvio = modalAberto && position.startsWith("top") ? 76 : undefined;

  return (
    <>
      <Toaster position={position} offset={desvio} />
      {target
        ? createPortal(
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                toast.dismiss();
              }}
              title={`Limpar todos os avisos (${target.count})`}
              data-testid="toasts-clear-all"
              data-aviso-layer="true"
              style={{
                position: "fixed",
                top: target.top,
                left: target.left,
                height: 22,
                minWidth: 22,
                padding: "0 6px",
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                borderRadius: "9999px",
                background: "#fff",
                border: "1px solid #e5e7eb",
                boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                color: "#6b7280",
                fontSize: 10,
                fontWeight: 700,
                lineHeight: 1,
                cursor: "pointer",
                zIndex: 2147483001,
                pointerEvents: "auto",
              }}
            >
              <ListX size={12} strokeWidth={2} />
              {target.count}
            </button>,
            document.body,
          )
        : null}
    </>
  );
}
